import { useCallback, useMemo } from 'react'
import { gql } from '@apollo/client'
import { showErrorToast, showSuccessToast } from '@npco/zeller-design-system'
import {
  useEditDebitCardTransactionImagesState,
  useGoToEditDebitCardTransactionImages,
} from 'features/EditDebitCardTransactionImages/edit-debit-card-transaction-images-routing'

import { useTransactionImages } from 'hooks/useTransactionImages'
import { useUploadedImages } from 'hooks/useUploadedImages'
import { translate } from 'utils/translations'
import { ImagesDebitCardTransactionV2Fragment } from 'types/gql-types/ImagesDebitCardTransactionV2Fragment'
import { useDisableNavigation } from 'components/DrawerWithNavigation/DrawerNavigation/hooks/useDisableNavigation/useDisableNavigation'
import { FileWithId } from 'components/File'
import { Images as ImagesDisplay } from 'components/Images/Images'

import { MAX_FILES } from '../../../../../DebitCardTransactions.utils'
import { ImagePreviewModal } from './ImagePreviewModal/ImagePreviewModal'
import { useSelectImages } from './Images.utils'

interface Props {
  transaction: ImagesDebitCardTransactionV2Fragment
}

export const Images = ({ transaction }: Props) => {
  const { id: transactionUuid } = transaction

  const { goToEditDebitCardTransactionImages } =
    useGoToEditDebitCardTransactionImages()

  const {
    isLoadingImages,
    isDownloadingFiles,
    images,
    removeImage: removePersistedImage,
  } = useTransactionImages({
    transactionUuid,
  })

  const { transactionCombinedImages, removeImage } = useUploadedImages({
    transactionUuid,
    fetchedImages: images,
  })

  const {
    previewId,
    setPreviewId,
    onNext,
    onPrevious,
    isSingleImage,
    onDelete,
    imageConfig,
  } = useSelectImages(transactionCombinedImages)

  const selectedImage = useMemo(
    () => transactionCombinedImages.find((image) => image.id === previewId),
    [transactionCombinedImages, previewId]
  )

  const { state } = useEditDebitCardTransactionImagesState()

  const isAddReceiptModalOpen = state !== null
  const isPreviewModalOpen = Boolean(selectedImage)

  useDisableNavigation({
    shouldDisableNavigation: isAddReceiptModalOpen || isPreviewModalOpen,
  })

  const closePreviewModal = useCallback(() => {
    return setPreviewId(null)
  }, [setPreviewId])

  const handleDelete = useCallback(
    (file: FileWithId) => {
      removeImage(file)
      removePersistedImage(file)
      showSuccessToast(
        translate('page.transactionDetails.images.preview.removeSuccess')
      )

      onDelete(file)
    },
    [removeImage, removePersistedImage, onDelete]
  )

  const allowedFilesLength = MAX_FILES - transactionCombinedImages.length
  const handleOnAdd = useCallback(() => {
    if (allowedFilesLength === 0) {
      showErrorToast(
        translate('page.transactionDetails.images.uploadLimitWarning')
      )
      return
    }

    goToEditDebitCardTransactionImages({
      stage: 'edit',
      debitCardTransactionUuid: transactionUuid,
      maxFiles: MAX_FILES,
    })
  }, [allowedFilesLength, transactionUuid, goToEditDebitCardTransactionImages])

  return (
    <>
      <ImagesDisplay
        title={translate('page.transactionDetails.images.title')}
        placeholder={translate('page.transactionDetails.images.placeholder')}
        onAdd={handleOnAdd}
        images={transactionCombinedImages}
        isLoadingImages={isLoadingImages}
        isDownloadingFiles={isDownloadingFiles}
        onClickThumbnail={setPreviewId}
      />
      {selectedImage && (
        <ImagePreviewModal
          image={selectedImage}
          transactionUuid={transactionUuid}
          isOpen={Boolean(selectedImage)}
          onDelete={handleDelete}
          onClose={closePreviewModal}
          onNext={onNext}
          onPrevious={onPrevious}
          images={transactionCombinedImages}
          previewId={previewId}
          setPreviewId={setPreviewId}
          onAdd={handleOnAdd}
          areImageControlsEnabled={!isSingleImage}
          imageConfig={imageConfig}
        />
      )}
    </>
  )
}

Images.fragments = {
  DebitCardTransactionV2: gql`
    fragment ImagesDebitCardTransactionV2Fragment on DebitCardTransactionV2 {
      id
    }
  `,
}
